// Voysi "Today's campaign" card. Visual reference: screens-1.jsx
// DashboardScreen (the right-column campaign card with flamingo gradient
// progress + 3 stat cells).
//
// The dashboard rev-1 doesn't have a live "active campaign" query yet, so
// this renders a calm empty-state design when no campaign is hot. When a
// campaign IS provided via props it shows the full progress UI. The
// DashboardPage today passes nothing (intentional placeholder); when the
// backend grows a `/campaigns/active-today` endpoint this swaps in cleanly.

interface CampaignStatProps {
  label: string
  value: string | number
  tone?: 'flamingo' | 'neutral'
}

function CampaignStat({ label, value, tone = 'neutral' }: CampaignStatProps) {
  const bg = tone === 'flamingo' ? '#FFF1F6' : '#F3F2EF'
  const fg = tone === 'flamingo' ? '#C4125C' : '#49474A'
  return (
    <div className="rounded-[10px] px-3 py-2.5" style={{ background: bg }}>
      <div
        className="text-[10px] font-bold uppercase tracking-[0.05em]"
        style={{ color: fg, opacity: 0.85 }}
      >
        {label}
      </div>
      <div className="mt-0.5 font-display text-[18px] font-extrabold leading-none tracking-tight text-ink-900">
        {value}
      </div>
    </div>
  )
}

export interface TodaysCampaign {
  name: string
  subline: string
  done: number
  total: number
  etaLabel: string
  qualified: number
  voicemail: number
  noAnswer: number
}

interface TodaysCampaignCardProps {
  campaign?: TodaysCampaign
}

export function TodaysCampaignCard({ campaign }: TodaysCampaignCardProps) {
  return (
    <div
      className="flex flex-col gap-3 rounded-2xl border border-ink-200 bg-white p-[18px]"
      style={{ boxShadow: 'var(--shadow-sm)' }}
    >
      <div className="font-display text-[14.5px] font-bold tracking-tight text-ink-900">
        Today's campaign
      </div>
      {campaign ? (
        <>
          <div>
            <div className="font-display text-[15px] font-bold text-ink-900">
              {campaign.name}
            </div>
            <div className="mt-0.5 text-[12px] text-ink-500">{campaign.subline}</div>
          </div>
          <div className="relative h-2.5 overflow-hidden rounded-full bg-ink-100">
            <div
              className="h-full rounded-full"
              style={{
                width: `${campaign.total > 0 ? Math.min((campaign.done / campaign.total) * 100, 100) : 0}%`,
                background: 'linear-gradient(90deg,#FF3D84,#FF7FAC)',
              }}
            />
          </div>
          <div className="flex justify-between text-[11.5px] text-ink-500">
            <span>
              {campaign.total > 0
                ? Math.round((campaign.done / campaign.total) * 100)
                : 0}
              % complete
            </span>
            <span>
              ETA <b className="text-ink-900">{campaign.etaLabel}</b>
            </span>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <CampaignStat label="Qualified" value={campaign.qualified} tone="flamingo" />
            <CampaignStat label="Voicemail" value={campaign.voicemail} />
            <CampaignStat label="No answer" value={campaign.noAnswer} />
          </div>
        </>
      ) : (
        <div className="text-[13px] text-ink-500">
          No campaign running today. Kick one off from the Campaigns page.
        </div>
      )}
    </div>
  )
}
